import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { drawTextLayerPreview } from '../engine/behindSubject';

function getContainedImageRect(containerWidth, containerHeight, imageWidth, imageHeight) {
    if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) {
        return { x: 0, y: 0, width: 0, height: 0 };
    }

    const containerAspect = containerWidth / containerHeight;
    const imageAspect = imageWidth / imageHeight;

    if (imageAspect > containerAspect) {
        const width = containerWidth;
        const height = containerWidth / imageAspect;
        return {
            x: 0,
            y: (containerHeight - height) / 2,
            width,
            height,
        };
    }

    const height = containerHeight;
    const width = containerHeight * imageAspect;
    return {
        x: (containerWidth - width) / 2,
        y: 0,
        width,
        height,
    };
}

export function displayPointToNormalized(localX, localY, imageRect) {
    return {
        x: imageRect.width ? (localX - imageRect.x) / imageRect.width : 0.5,
        y: imageRect.height ? (localY - imageRect.y) / imageRect.height : 0.5,
    };
}

export function getImageRectWithinContainer(containerElement, imageElement, imageWidth, imageHeight) {
    if (containerElement && imageElement) {
        const containerRect = containerElement.getBoundingClientRect();
        const imageRect = imageElement.getBoundingClientRect();

        if (imageRect.width > 0 && imageRect.height > 0) {
            return {
                x: imageRect.left - containerRect.left,
                y: imageRect.top - containerRect.top,
                width: imageRect.width,
                height: imageRect.height,
            };
        }
    }

    if (!containerElement) {
        return { x: 0, y: 0, width: 0, height: 0 };
    }

    const { width, height } = containerElement.getBoundingClientRect();
    return getContainedImageRect(width, height, imageWidth, imageHeight);
}

class LayerDragPreview extends Component {
    static propTypes = {
        layer: PropTypes.object.isRequired,
        imageRectWidth: PropTypes.number.isRequired,
        opacity: PropTypes.number,
    };

    static defaultProps = {
        opacity: 0.65,
    };

    canvasRef = React.createRef();

    componentDidMount() {
        this.redraw();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.layer !== this.props.layer
            || prevProps.imageRectWidth !== this.props.imageRectWidth
            || prevProps.opacity !== this.props.opacity) {
            this.redraw();
        }
    }

    redraw() {
        const canvas = this.canvasRef.current;
        if (!canvas) {
            return;
        }

        drawTextLayerPreview(canvas, this.props.layer, this.props.imageRectWidth, this.props.opacity);
    }

    render() {
        const { opacity } = this.props;
        return (
            <canvas
                ref={this.canvasRef}
                className="block max-w-none"
                style={{ opacity: opacity < 0.1 ? 0 : opacity }}
            />
        );
    }
}

export default class BehindLayersOverlay extends Component {
    static defaultProps = {
        layers: [],
        enabled: false,
        imageWidth: 0,
        imageHeight: 0,
        getImageElement: null,
        onLayerDrag: () => {},
        onLayerDragStart: () => {},
        onLayerDragEnd: () => {},
    };

    static propTypes = {
        layers: PropTypes.array,
        enabled: PropTypes.bool,
        imageWidth: PropTypes.number,
        imageHeight: PropTypes.number,
        getImageElement: PropTypes.func,
        onLayerDrag: PropTypes.func,
        onLayerDragStart: PropTypes.func,
        onLayerDragEnd: PropTypes.func,
    };

    state = {
        draggingLayerId: null,
        dragOffsetX: 0,
        dragOffsetY: 0,
        dragX: 0,
        dragY: 0,
        layoutVersion: 0,
    };

    containerRef = React.createRef();
    layoutObserver = null;
    observedImage = null;
    activePointerId = null;

    componentDidMount() {
        this.bindLayoutObservers();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.layers !== this.props.layers
            || prevProps.enabled !== this.props.enabled
            || prevProps.getImageElement !== this.props.getImageElement) {
            this.bindLayoutObservers();
            this.bumpLayoutVersion();
        }
    }

    componentWillUnmount() {
        this.unbindLayoutObservers();
    }

    bumpLayoutVersion = () => {
        this.setState(prevState => ({
            layoutVersion: prevState.layoutVersion + 1,
        }));
    };

    bindLayoutObservers = () => {
        this.unbindLayoutObservers();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.layoutObserver = new ResizeObserver(this.bumpLayoutVersion);

        if (this.containerRef.current) {
            this.layoutObserver.observe(this.containerRef.current);
        }

        const imageElement = this.props.getImageElement?.();
        if (imageElement) {
            this.observedImage = imageElement;
            this.layoutObserver.observe(imageElement);
            imageElement.addEventListener('load', this.bumpLayoutVersion);
        }
    };

    unbindLayoutObservers = () => {
        if (this.layoutObserver) {
            this.layoutObserver.disconnect();
            this.layoutObserver = null;
        }

        if (this.observedImage) {
            this.observedImage.removeEventListener('load', this.bumpLayoutVersion);
            this.observedImage = null;
        }
    };

    getImageRect() {
        return getImageRectWithinContainer(
            this.containerRef.current,
            this.props.getImageElement?.(),
            this.props.imageWidth,
            this.props.imageHeight
        );
    }

    layerToDisplayPoint(layer) {
        const rect = this.getImageRect();
        return {
            left: rect.x + (layer.x || 0) * rect.width,
            top: rect.y + (layer.y || 0) * rect.height,
        };
    }

    startDrag = (clientX, clientY, layer) => {
        if (!this.containerRef.current) {
            return;
        }

        const point = this.layerToDisplayPoint(layer);
        const containerRect = this.containerRef.current.getBoundingClientRect();
        this.setState({
            draggingLayerId: layer.id,
            dragOffsetX: clientX - containerRect.left - point.left,
            dragOffsetY: clientY - containerRect.top - point.top,
            dragX: point.left,
            dragY: point.top,
        });
        this.props.onLayerDragStart(layer.id);
    };

    updateDrag = (clientX, clientY) => {
        if (!this.state.draggingLayerId || !this.containerRef.current) {
            return;
        }

        const containerRect = this.containerRef.current.getBoundingClientRect();
        this.setState({
            dragX: clientX - containerRect.left - this.state.dragOffsetX,
            dragY: clientY - containerRect.top - this.state.dragOffsetY,
        });
    };

    finishDrag = () => {
        const { draggingLayerId, dragX, dragY } = this.state;
        if (!draggingLayerId) {
            return;
        }

        const normalized = displayPointToNormalized(dragX, dragY, this.getImageRect());
        this.props.onLayerDrag(draggingLayerId, normalized.x, normalized.y);
        this.props.onLayerDragEnd(draggingLayerId);
        this.setState({
            draggingLayerId: null,
        });
    };

    handlePointerDown = (event, layer) => {
        if (!this.containerRef.current) {
            return;
        }

        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        this.activePointerId = event.pointerId;
        this.startDrag(event.clientX, event.clientY, layer);
    };

    handlePointerMove = (event) => {
        if (!this.state.draggingLayerId || event.pointerId !== this.activePointerId) {
            return;
        }

        event.preventDefault();
        this.updateDrag(event.clientX, event.clientY);
    };

    handlePointerUp = (event) => {
        if (!this.state.draggingLayerId || event.pointerId !== this.activePointerId) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        this.activePointerId = null;
        this.finishDrag();
    };

    handlePointerCancel = (event) => {
        if (event.pointerId !== this.activePointerId) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        const { draggingLayerId } = this.state;
        this.activePointerId = null;
        this.setState({ draggingLayerId: null });
        if (draggingLayerId) {
            this.props.onLayerDragEnd(draggingLayerId);
        }
    };

    renderLayerPreview(layer, isDragging) {
        const imageRect = this.getImageRect();

        if (layer.type === 'text') {
            return (
                <LayerDragPreview
                    layer={layer}
                    imageRectWidth={imageRect.width || 100}
                    opacity={isDragging ? 0.65 : 0}
                />
            );
        }

        if (layer.type === 'image' && layer.imageSrc) {
            const scale = layer.scale || 1;
            const width = (imageRect.width || 100) * scale;
            return (
                <img
                    src={layer.imageSrc}
                    alt={layer.name || 'Layer'}
                    className="block max-w-none"
                    style={{
                        opacity: isDragging ? 0.65 : 0,
                        width,
                        height: 'auto',
                    }}
                />
            );
        }

        return null;
    }

    render() {
        const { layers, enabled } = this.props;
        const { draggingLayerId, dragX, dragY, layoutVersion } = this.state;

        if (!enabled || !layers.length) {
            return null;
        }

        void layoutVersion;

        return (
            <div ref={this.containerRef} className="pointer-events-none absolute inset-0 z-20">
                { layers.filter(layer => layer.visible !== false).map(layer => {
                    const isDragging = draggingLayerId === layer.id;
                    const point = isDragging
                        ? { left: dragX, top: dragY }
                        : this.layerToDisplayPoint(layer);
                    const rotation = layer.rotation || 0;

                    return (
                        <div
                            key={layer.id}
                            className={cx(
                                'absolute touch-none pointer-events-auto select-none',
                                isDragging ? 'z-30 cursor-grabbing' : 'z-10 cursor-grab'
                            )}
                            style={{
                                left: point.left,
                                top: point.top,
                                transform: `translate(-50%, -50%) rotate(${isDragging ? -rotation : 0}deg)`,
                            }}
                            onPointerDown={event => this.handlePointerDown(event, layer)}
                            onPointerMove={this.handlePointerMove}
                            onPointerUp={this.handlePointerUp}
                            onPointerCancel={this.handlePointerCancel}
                        >
                            <div className="relative inline-block">
                                { this.renderLayerPreview(layer, isDragging) }
                                { !isDragging && (
                                    <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-4 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-primary bg-white shadow-sm" />
                                ) }
                            </div>
                        </div>
                    );
                }) }
            </div>
        );
    }
}
